<template lang="pug">
div(v-if="active" class="howto-overlay" @click.self="skip")
  //- Spotlight: a box around the target whose huge box-shadow dims everything else
  div(v-if="rect" class="howto-spot" :style="spotStyle")
  div(class="howto-card" :style="cardStyle" :class="{ 'howto-card--center': !rect }")
    div(class="howto-step") Step {{ index + 1 }} of {{ steps.length }}
    div(class="howto-title")
      Icon(:icon="step.icon || 'tabler:hand-click'" class="mr-2")
      | {{ step.title }}
    p(class="howto-text" v-html="step.text")
    div(class="howto-actions")
      b-button(size="sm" variant="link" class="howto-skip p-0" @click="skip") Skip tour
      div(class="ml-auto")
        b-button(size="sm" variant="outline-secondary" class="mr-2" v-if="index > 0" @click="prev") Back
        b-button(size="sm" variant="primary" @click="next") {{ index === steps.length - 1 ? 'Done' : 'Next' }}
</template>

<script>
// Skippable "what to press, where to go" walkthrough — same component as the
// caregiver app's HowToTour. Each step may point at an element marked
// data-tour="<target>"; it is scrolled into view and spotlighted. Shown once
// per storageKey (localStorage) when autoStart is set, replayable via start().
import { Icon } from '@iconify/vue2';

export default {
  name: 'HowToTour',
  components: { Icon },
  props: {
    steps: { type: Array, required: true }, // [{ title, text, target?, icon? }]
    storageKey: { type: String, required: true },
    autoStart: { type: Boolean, default: false },
    autoStartDelay: { type: Number, default: 900 },
  },
  data() {
    return { active: false, index: 0, rect: null, cardTop: null };
  },
  computed: {
    step() { return this.steps[this.index] || {}; },
    spotStyle() {
      const r = this.rect;
      return { top: `${r.top - 6}px`, left: `${r.left - 6}px`, width: `${r.width + 12}px`, height: `${r.height + 12}px` };
    },
    cardStyle() {
      return this.rect && this.cardTop != null ? { top: `${this.cardTop}px` } : {};
    },
  },
  mounted() {
    let seen = false;
    try { seen = localStorage.getItem(this.storageKey) === '1'; } catch (e) { /* storage blocked */ }
    if (this.autoStart && !seen) setTimeout(() => this.start(), this.autoStartDelay);
    window.addEventListener('resize', this.measure);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.measure);
  },
  methods: {
    start() {
      this.index = 0;
      this.active = true;
      this.$nextTick(() => this.focusStep());
    },
    next() {
      if (this.index >= this.steps.length - 1) return this.finish();
      this.index += 1;
      this.focusStep();
    },
    prev() {
      if (this.index > 0) { this.index -= 1; this.focusStep(); }
    },
    skip() { this.finish(); },
    finish() {
      this.active = false;
      this.rect = null;
      try { localStorage.setItem(this.storageKey, '1'); } catch (e) { /* ignore */ }
      this.$emit('done');
    },
    targetEl() {
      if (!this.step.target) return null;
      return document.querySelector(`[data-tour="${this.step.target}"]`);
    },
    focusStep() {
      const el = this.targetEl();
      if (!el) { this.rect = null; return; }
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(this.measure, 350);
      setTimeout(this.measure, 800);
    },
    measure() {
      const el = this.targetEl();
      if (!el || !this.active) { this.rect = null; return; }
      const r = el.getBoundingClientRect();
      if (!r.width && !r.height) { this.rect = null; return; }
      this.rect = { top: r.top, left: r.left, width: r.width, height: r.height };
      const cardH = 200;
      const below = r.bottom + 14;
      this.cardTop = below + cardH < window.innerHeight ? below : Math.max(12, r.top - cardH - 14);
    },
  },
};
</script>

<style lang="scss" scoped>
.howto-overlay { position: fixed; inset: 0; z-index: 3000; }
.howto-spot {
  position: fixed; border-radius: 12px;
  box-shadow: 0 0 0 9999px rgba(20, 16, 30, 0.6);
  border: 2px solid #ffd166; pointer-events: none;
  transition: top 0.25s, left 0.25s, width 0.25s, height 0.25s;
}
.howto-card {
  position: fixed; left: 12px; right: 12px; max-width: 460px; margin: 0 auto;
  background: #fff; border-radius: 14px; padding: 0.9rem 1rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35); text-align: left;
}
.howto-card--center {
  top: 50%; transform: translateY(-50%);
  box-shadow: 0 0 0 9999px rgba(20, 16, 30, 0.6), 0 10px 30px rgba(0, 0, 0, 0.35);
}
.howto-step { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #6b4c9a; }
.howto-title { font-weight: 800; font-size: 1rem; margin: 0.2rem 0 0.3rem; display: flex; align-items: center; }
.howto-text { font-size: 0.9rem; color: #333; margin-bottom: 0.7rem; }
.howto-actions { display: flex; align-items: center; }
.howto-skip { font-size: 0.8rem; color: #888; }
</style>
